import { useEffect, useMemo, useState } from "react";
import {
  ArrowUpRight,
  ArrowDownRight,
  ClipboardList,
  MapPin,
  Bell,
  FileText,
  AlertTriangle,
  Users,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { api } from "@/lib/api";

const STATUS_HEX = {
  "Site Visited": "#3b82f6",
  "Materials Delivered": "#a855f7",
  "Work in Progress": "#f59e0b",
  "Completed": "#10b981",
  "On Hold": "#78716c",
  "Cancelled": "#f43f5e",
};

/**
 * Admin overview page — greeting, KPI cards, and charts computed from the
 * submissions dataset already loaded by AdminDashboard.
 *
 * Props:
 *  - user: {name,email}
 *  - submissions: [] full list already loaded by parent (single source of truth)
 *  - workersCount: number
 *  - rejectedCount: number
 */
export default function AdminOverview({ user, submissions, workersCount, rejectedCount }) {
  const [quotesCount, setQuotesCount] = useState(0);
  const [remindersCount, setRemindersCount] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [q, r] = await Promise.all([
          api.get("/quotations"),
          api.get("/reminders"),
        ]);
        setQuotesCount(q.data?.length || 0);
        const c = r.data?.counts || {};
        setRemindersCount((c.today || 0) + (c.tomorrow || 0) + (c.day_after || 0));
      } catch (e) { /* silent; page still usable */ }
    })();
  }, []);

  const now = new Date();
  const greet = now.getHours() < 12 ? "Good morning" : now.getHours() < 17 ? "Good afternoon" : "Good evening";
  const displayName = user?.name || user?.email?.split("@")[0] || "Admin";

  // ---- KPIs ----
  const kpis = useMemo(() => {
    const total = submissions.length;
    const withGps = submissions.filter((s) => s.geo?.latitude != null).length;
    const today = new Date().toISOString().slice(0, 10);
    const todayCount = submissions.filter((s) => (s.created_at || "").slice(0, 10) === today).length;
    return { total, withGps, todayCount };
  }, [submissions]);

  // ---- Chart data ----
  const barData = useMemo(() => {
    // Last 14 days submission volume
    const buckets = {};
    for (let i = 13; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      buckets[key] = { date: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }), key, visits: 0 };
    }
    for (const s of submissions) {
      const k = (s.created_at || "").slice(0, 10);
      if (buckets[k]) buckets[k].visits += 1;
    }
    return Object.values(buckets);
  }, [submissions]);

  const statusData = useMemo(() => {
    const m = {};
    for (const s of submissions) {
      const st = s.status || "Site Visited";
      m[st] = (m[st] || 0) + 1;
    }
    return Object.entries(m).map(([name, value]) => ({ name, value }));
  }, [submissions]);

  const topWorkers = useMemo(() => {
    const m = {};
    for (const s of submissions) {
      const n = s.worker_name || "—";
      m[n] = (m[n] || 0) + 1;
    }
    return Object.entries(m).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 5);
  }, [submissions]);

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900">
            {greet}, {displayName}! <span className="inline-block ml-1">👋</span>
          </h1>
          <p className="text-sm text-slate-500 mt-1">Here&apos;s what&apos;s happening at {onSite(kpis.total)} today.</p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 self-start">
          <span className="text-blue-600">📅</span>
          {new Date(new Date().setDate(new Date().getDate() - 14)).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
          {" — "}
          {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <KpiCard title="Total Submissions" value={kpis.total} icon={ClipboardList} tint="blue" delta={pct(kpis.total, kpis.total - kpis.todayCount)} testId="kpi-submissions" />
        <KpiCard title="Today's Visits"    value={kpis.todayCount} icon={MapPin} tint="emerald" delta={kpis.todayCount > 0 ? "New" : "0%"} deltaDir={kpis.todayCount > 0 ? "up" : "flat"} testId="kpi-today" />
        <KpiCard title="Pending Reminders" value={remindersCount} icon={Bell} tint="amber" delta="Next 3 days" deltaDir="flat" testId="kpi-reminders" />
        <KpiCard title="Total Quotations"  value={quotesCount} icon={FileText} tint="violet" delta={quotesCount > 0 ? "Live" : "0%"} deltaDir="up" testId="kpi-quotes" />
        <KpiCard title="Rejected Attempts" value={rejectedCount} icon={AlertTriangle} tint="rose" delta={rejectedCount > 0 ? "Review" : "0%"} deltaDir={rejectedCount > 0 ? "down" : "flat"} testId="kpi-rejected" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Visits overview */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display font-bold text-slate-900">Site Visits Overview</h3>
              <p className="text-xs text-slate-500">Last 14 days</p>
            </div>
            <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-lg px-2.5 py-1">Last 14 days</span>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={barData} margin={{ top: 10, right: 10, left: -14, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(59,130,246,0.08)" }} contentStyle={{ borderRadius: 10, borderColor: "#e2e8f0" }} />
                <Bar dataKey="visits" fill="url(#barBlue)" radius={[6, 6, 0, 0]} />
                <defs>
                  <linearGradient id="barBlue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#1e40af" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status donut */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-display font-bold text-slate-900">By Status</h3>
          <p className="text-xs text-slate-500 mb-2">Distribution of every submission</p>
          {statusData.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">No data yet</div>
          ) : (
            <div style={{ width: "100%", height: 250 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={statusData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={2}
                  >
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={STATUS_HEX[entry.name] || "#94a3b8"} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 10, borderColor: "#e2e8f0", fontSize: 12 }} />
                  <Legend
                    verticalAlign="bottom"
                    iconType="circle"
                    wrapperStyle={{ fontSize: 11 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Top workers */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display font-bold text-slate-900 inline-flex items-center gap-2"><Users className="w-4 h-4 text-blue-600" /> Top Field Workers</h3>
            <p className="text-xs text-slate-500">By total submissions</p>
          </div>
          <span className="text-xs font-semibold text-blue-700">{workersCount} active</span>
        </div>
        {topWorkers.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400">No submissions yet.</div>
        ) : (
          <div className="space-y-2">
            {topWorkers.map((w, i) => {
              const max = topWorkers[0].count;
              const pctBar = Math.max(6, Math.round((w.count / max) * 100));
              return (
                <div key={w.name} data-testid={`top-worker-${i}`} className="flex items-center gap-3">
                  <div className="w-8 h-8 shrink-0 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-slate-800 truncate">{w.name}</div>
                      <div className="text-xs font-bold text-slate-600 shrink-0">{w.count}</div>
                    </div>
                    <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-blue-700 rounded-full" style={{ width: `${pctBar}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function onSite(n) {
  if (!n) return "your business";
  return `your business (${n} total visits)`;
}
function pct(cur, prev) {
  if (!prev || prev <= 0) return cur > 0 ? "New" : "0%";
  const d = ((cur - prev) / prev) * 100;
  const s = (d >= 0 ? "+" : "") + d.toFixed(1) + "%";
  return s;
}

function KpiCard({ title, value, icon: Icon, tint = "blue", delta, deltaDir, testId }) {
  const TINTS = {
    blue: "bg-blue-100 text-blue-700",
    emerald: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    violet: "bg-violet-100 text-violet-700",
    rose: "bg-rose-100 text-rose-700",
  };
  const dirUp = deltaDir !== "down" && !String(delta).startsWith("-");
  return (
    <div data-testid={testId} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <span className={`w-10 h-10 rounded-xl inline-flex items-center justify-center ${TINTS[tint]}`}>
          <Icon className="w-5 h-5" />
        </span>
      </div>
      <div className="text-[11px] font-semibold text-slate-500 mt-3 leading-tight">{title}</div>
      <div className="mt-1 text-3xl font-black text-slate-900 tracking-tight">{value}</div>
      <div className={`mt-2 inline-flex items-center gap-1 text-[11px] font-semibold ${dirUp ? "text-emerald-600" : "text-rose-600"}`}>
        {deltaDir === "flat" ? null : dirUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
        <span>{delta}</span>
        <span className="text-slate-400 font-normal">vs prev</span>
      </div>
    </div>
  );
}
